import React from "react";
import { ethers } from "ethers";
import { useWeb3 } from "../context/Web3Context";
import { useReads } from "../hooks/useReads";
import { getCoreDex, getUsdt } from "../web3/contracts";
import { fmtUsdt, fmtUnits, shortAddress, parseUsdt } from "../utils/format";
import { ACTIVE_CONTRACTS, CORE_DECIMALS } from "../config/app.config";
import StatGrid from "../components/admin/StatGrid";
import ActionForm from "../components/admin/ActionForm";
import PauseControl from "../components/admin/PauseControl";
import RoleBanner from "../components/admin/RoleBanner";
import AddressBar from "../components/admin/AddressBar";

const addr = (k, label, ph = "0x...") => ({ key: k, label, placeholder: ph });

export default function CoreDexAdmin() {
  const { signer, account } = useWeb3();
  const { data, reload } = useReads(async (provider) => {
    const d = getCoreDex(provider);
    const [owner, admin, paused, liq, circ, price, burned, physical, surplus, coreToken, usdt] = await Promise.all([
      d.owner(), d.admin(), d.paused(), d.liquidityUSDT(), d.effectiveCirculation(),
      d.getPrice(), d.coreBurnedTotal(), d.usdtPhysicalBalance(), d.usdtSurplus(), d.coreToken(), d.usdt(),
    ]);
    return { owner, admin, paused, liq, circ, price, burned, physical, surplus, coreToken, usdt };
  });

  const C = (s) => getCoreDex(s);

  // Add liquidity = approve USDT (if needed) then addLiquidity.
  const addLiquidity = async (v) => {
    const amt = parseUsdt(v.amt);
    const usdt = getUsdt(signer);
    const allowance = await usdt.allowance(account, ACTIVE_CONTRACTS.coreDex);
    if (allowance.lt(amt)) {
      const ap = await usdt.approve(ACTIVE_CONTRACTS.coreDex, ethers.constants.MaxUint256);
      await ap.wait();
    }
    return C(signer).addLiquidity(amt);
  };

  return (
    <div className="stack">
      <AddressBar label="CORE DEX Contract" address={ACTIVE_CONTRACTS.coreDex} />
      <RoleBanner owner={data?.owner} admin={data?.admin} />

      <StatGrid
        title="CORE DEX — Overview" icon="layers" accent="violet" onRefresh={reload}
        rows={data ? [
          { label: "Owner", value: shortAddress(data.owner), mono: true },
          { label: "Admin", value: shortAddress(data.admin), mono: true },
          { label: "Paused", value: data.paused ? "Yes" : "No" },
          { label: "Price", value: data.price.isZero() ? "Awaiting liquidity" : `$${fmtUsdt(data.price, 4)}` },
          { label: "Liquidity (book)", value: `${fmtUsdt(data.liq)} USDT` },
          { label: "Physical USDT", value: `${fmtUsdt(data.physical)} USDT` },
          { label: "USDT Surplus", value: `${fmtUsdt(data.surplus)} USDT` },
          { label: "Effective CORE", value: fmtUnits(data.circ, CORE_DECIMALS, 2) },
          { label: "CORE Burned", value: fmtUnits(data.burned, CORE_DECIMALS, 2) },
          { label: "CORE Token", value: shortAddress(data.coreToken), mono: true },
          { label: "USDT", value: shortAddress(data.usdt), mono: true },
        ] : []}
      />

      <PauseControl paused={data?.paused} getContract={C} signer={signer} onDone={reload} />

      <div className="adm-section-title">Liquidity & pricing</div>
      <div className="adm-grid">
        <ActionForm title="Add Liquidity" desc="Approves USDT then deposits into the pool." icon="coins" accent="success"
          fields={[{ key: "amt", label: "Amount (USDT)", placeholder: "1000", numeric: true }]}
          button="Add Liquidity" onDone={reload} onRun={addLiquidity} />

        <ActionForm title="Set Price Manually" desc="Reconcile liquidity to target price (needs USDT backing)." icon="gauge" accent="gold"
          fields={[{ key: "p", label: "Price (USDT)", placeholder: "0.05", numeric: true }]}
          button="Set Price" onDone={reload}
          onRun={(v) => C(signer).setPriceManually(parseUsdt(v.p))} />

        <ActionForm title="Sweep CORE to Dead" desc="Burns any CORE accidentally held by the DEX." icon="spark"
          fields={[]} button="Sweep CORE" onDone={reload}
          onRun={() => C(signer).sweepCoreToDead()} />
      </div>

      <div className="adm-section-title">Withdraw & roles</div>
      <div className="adm-grid">
        <ActionForm title="Emergency Withdraw" desc="Pull USDT from liquidity (owner)." icon="shield" danger
          fields={[addr("a", "To address"), { key: "amt", label: "Amount (USDT)", placeholder: "100", numeric: true }]}
          button="Withdraw USDT" onDone={reload}
          onRun={(v) => C(signer).emergencyWithdraw(v.a, parseUsdt(v.amt))} />

        <ActionForm title="Sweep Surplus" desc="Withdraw unaccounted USDT (owner)." icon="coins" accent="gold"
          fields={[addr("a", "To address"), { key: "amt", label: "Amount (USDT)", placeholder: "100", numeric: true }]}
          button="Sweep Surplus" onDone={reload}
          onRun={(v) => C(signer).sweepSurplus(v.a, parseUsdt(v.amt))} />

        <ActionForm title="Set Admin (owner)" icon="shield" fields={[addr("a", "New admin address")]} button="Set Admin" onDone={reload} onRun={(v) => C(signer).setAdmin(v.a)} />

        <ActionForm title="Transfer Ownership" desc="Irreversible." icon="shield" danger
          fields={[addr("a", "New owner address")]} button="Transfer Ownership" onDone={reload}
          onRun={(v) => C(signer).transferOwnership(v.a)} />
      </div>
    </div>
  );
}
